import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { 
  Phone, 
  MapPin, 
  AlertTriangle, 
  Ambulance,
  Share2,
  CheckCircle,
  ChevronRight,
  Info,
  Clock
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';

export interface EmergencyServicesConnectProps {
  patientName?: string;
  patientLocation?: {
    address: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    }
  };
  symptoms?: string[];
  urgencyLevel: 'immediate' | 'urgent';
  className?: string;
}

export function EmergencyServicesConnect({
  patientName = 'Patient',
  patientLocation,
  symptoms = [],
  urgencyLevel,
  className
}: EmergencyServicesConnectProps) {
  const [activeTab, setActiveTab] = useState<string>('call');
  const [callInitiated, setCallInitiated] = useState<boolean>(false);
  const [locationShared, setLocationShared] = useState<boolean>(false);

  const handleEmergencyCall = () => {
    // In a real implementation, this would use the Web Telephony API
    // or other appropriate method to initiate the call
    window.location.href = 'tel:911';
    setCallInitiated(true);
  };

  const handleShareLocation = () => {
    // In a real implementation, this would use the Geolocation API
    // to get the user's current location and share it
    setLocationShared(true);
  };

  const emergencyFacilities = [
    {
      name: 'Central Hospital Emergency',
      distance: '1.2 miles',
      address: '123 Medical Center Blvd',
      phone: '(555) 123-4567',
      waitTime: '5-10 minutes'
    },
    {
      name: 'Westside Urgent Care',
      distance: '2.4 miles',
      address: '456 Healthcare Drive',
      phone: '(555) 234-5678',
      waitTime: '15-20 minutes'
    },
    {
      name: 'Eastside Emergency Clinic',
      distance: '3.1 miles',
      address: '789 Emergency Road',
      phone: '(555) 345-6789',
      waitTime: '10-15 minutes'
    }
  ];

  return (
    <Card className={cn('border-2', urgencyLevel === 'immediate' ? 'border-red-500' : 'border-orange-500', className)}>
      <CardHeader className={urgencyLevel === 'immediate' ? 'bg-red-50' : 'bg-orange-50'}>
        <div className="flex items-center gap-2">
          <AlertTriangle className={cn('w-6 h-6', urgencyLevel === 'immediate' ? 'text-red-600' : 'text-orange-600')} />
          <CardTitle className={cn(urgencyLevel === 'immediate' ? 'text-red-800' : 'text-orange-800')}>
            {urgencyLevel === 'immediate' ? 'Emergency Assistance' : 'Urgent Assistance'}
          </CardTitle>
        </div>
        <CardDescription className="text-base">
          {urgencyLevel === 'immediate' 
            ? 'Your symptoms require immediate emergency attention.' 
            : 'Your symptoms require urgent medical attention.'}
        </CardDescription>
      </CardHeader>
      
      <Tabs defaultValue="call" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-3 mx-4 mt-4">
          <TabsTrigger value="call" className="flex items-center gap-2">
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">Call</span>
          </TabsTrigger>
          <TabsTrigger value="locate" className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            <span className="hidden sm:inline">Locate</span>
          </TabsTrigger>
          <TabsTrigger value="ambulance" className="flex items-center gap-2">
            <Ambulance className="w-4 h-4" />
            <span className="hidden sm:inline">Ambulance</span>
          </TabsTrigger>
        </TabsList>
        
        <CardContent className="pt-6">
          <TabsContent value="call" className="space-y-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
              <h3 className="font-medium text-lg">Emergency Services</h3>
              <p className="text-gray-600">
                Call 911 immediately to connect with emergency services. Be prepared to share your symptoms and location.
              </p>
              
              {callInitiated && (
                <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-md">
                  <CheckCircle className="w-5 h-5" />
                  <span>Call initiated. Stay on the line with emergency services.</span>
                </div>
              )}
              
              <div className="space-y-2">
                <h4 className="font-medium text-gray-700">When calling, mention:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span>Your name and location</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span>Your symptoms: {symptoms.join(', ')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span>Any medications you're taking</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span>Any allergies you have</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <Button 
              onClick={handleEmergencyCall}
              className="w-full py-6 text-lg bg-red-600 hover:bg-red-700 flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Call 911 Now
            </Button>
          </TabsContent>
          
          <TabsContent value="locate" className="space-y-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h3 className="font-medium text-lg mb-3">Nearest Emergency Facilities</h3>
              
              <div className="space-y-4">
                {emergencyFacilities.map((facility, index) => (
                  <div key={index} className="border border-gray-200 rounded-md p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">{facility.name}</h4>
                      <Badge variant="outline" className="bg-blue-50">
                        {facility.distance}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-600">{facility.address}</div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="text-blue-600">{facility.phone}</div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span>Wait: {facility.waitTime}</span>
                      </div>
                    </div>
                    <div className="pt-2 flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => window.location.href = `tel:${facility.phone.replace(/[^0-9]/g, '')}`}
                      >
                        <Phone className="w-4 h-4 mr-1" /> Call
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(facility.address)}`, '_blank')}
                      >
                        <MapPin className="w-4 h-4 mr-1" /> Directions
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="ambulance" className="space-y-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
              <h3 className="font-medium text-lg">Request Ambulance</h3>
              <p className="text-gray-600">
                If you're unable to travel safely to an emergency facility, request an ambulance.
              </p>
              
              {patientLocation ? (
                <div className="bg-gray-50 p-3 rounded-md">
                  <div className="font-medium">Your Location</div>
                  <div className="text-gray-600">{patientLocation.address}</div>
                </div>
              ) : (
                <div className="flex items-center gap-2 bg-yellow-50 p-3 rounded-md text-yellow-800">
                  <Info className="w-5 h-5 text-yellow-600" />
                  <span>Share your location to help emergency services find you quickly.</span>
                </div>
              )}
              
              {locationShared && (
                <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-md">
                  <CheckCircle className="w-5 h-5" />
                  <span>Location shared successfully.</span>
                </div>
              )}
              
              <div className="flex items-center gap-2 bg-blue-50 p-3 rounded-md text-blue-800">
                <AlertTriangle className="w-5 h-5 text-blue-600" />
                <span>Ambulance services may have associated costs. Check with your insurance provider.</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button 
                onClick={handleEmergencyCall}
                className="py-6 bg-red-600 hover:bg-red-700 flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Call 911
              </Button>
              
              <Button 
                onClick={handleShareLocation} 
                variant={locationShared ? "outline" : "default"}
                className="py-6 flex items-center justify-center gap-2"
                disabled={locationShared}
              >
                {locationShared ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Location Shared
                  </>
                ) : (
                  <>
                    <Share2 className="w-5 h-5" />
                    Share Location
                  </>
                )}
              </Button>
            </div>
          </TabsContent>
        </CardContent>
      </Tabs>
      
      <Separator />
      
      <CardFooter className="p-4 bg-gray-50">
        <div className="w-full text-center text-sm text-gray-600">
          If you are experiencing a life-threatening emergency, please call 911 immediately or go to your nearest emergency room.
        </div>
      </CardFooter>
    </Card>
  );
}
